import { Descriptions as AntdDescriptions, Collapse, Divider, List, Rate, Table } from "antd";
import { IQuantity } from "../../../models/product/response";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import ProductContext from "../../../context/product/context";
import ProductContextProvider from "../../../context/product/provider";
import { ImCheckmark, ImCross } from "react-icons/im";
import Tag from "../../general/antd/tag";
import {
  getProductStatus,
  getProductStatusColor,
  getProductType,
  getProductTypeColor,
} from "../../../models/product/enum";
import ProductAttributes from "../attribute/product-attributes";
import OldImage from "../../form-components/old-image";

interface Props { }
const desc = ["terrible", "bad", "normal", "good", "wonderful"];

const Product: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const { details, actions, loading } = useContext(ProductContext);
  const { id } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, [direction]);
  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("product")}
        buttonName={`${t("update_product")}`}
        onClick={() => navigate(`/products/update/${id}`)}
        loading={loading.includes('details')}
        onRefresh={() => actions.getDetails(Number(id))}
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <>
            <Descriptions size="small" column={1} bordered>
              <AntdDescriptions.Item label={t("id")}>
                {details?.id}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("barcode")}>
                {details?.barcode}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("name")}>
                {details?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("extra_name")}>
                {details?.extraName ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("store")}>
                {details?.store?.name ?? "-"}
              </AntdDescriptions.Item>

              <AntdDescriptions.Item label={t("ratesCount")}>
                {details?.ratesCount ?? "-"}
              </AntdDescriptions.Item>

              <AntdDescriptions.Item label={t("rate")}>
                <Rate tooltips={desc} disabled defaultValue={details?.rates} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("free_delivery")}>
                {details?.freeDelivery ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>

              <AntdDescriptions.Item label={t("need_recipe")}>
                {details?.needRecipe ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("sold_by_unit")}>
                {details?.soldByUnit ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("note")}>
                {details?.note ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("description")}>
                {details?.description ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: details?.description }}
                  />
                ) : (
                  "-"
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("slice")}>
                {details?.slice?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("main_category")}>
                {details?.mainCategory?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("parent_category")}>
                {details?.parentCategory?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("childern_category")}>
                {details?.childCategory?.name ?? "-"}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("status")}>
                <Tag
                  color={getProductStatusColor(details?.status)}
                  title={t(getProductStatus(details?.status))}
                />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("unit")}>
                <Tag title={details?.unit?.name ?? "-"} />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("type")}>
                <Tag
                  color={getProductTypeColor(details?.type)}
                  title={t(getProductType(details?.type))}
                />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("tags")}>
                {details?.tags?.length > 0
                  ? details?.tags?.map((tag) => {
                    return <Tag title={tag?.name} color={tag?.color} />;
                  })
                  : "-"}

              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("unit_partitions")}>
                {details?.unitPartition?.length > 0
                  ? details?.unitPartition?.map((unitPartition) => {
                    return <Tag title={unitPartition?.name} />;
                  })
                  : "-"}

              </AntdDescriptions.Item>
            </Descriptions>

            <Collapse style={{ marginTop: 16 }}>
              <Collapse.Panel header={t("store_prices")} key="store-prices">
                <Table<IQuantity>
                  dataSource={details?.storeQuantities || []}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: t("store"),
                      dataIndex: "store_name",
                      key: "store_name",
                      render: (name: string | { ar: string; en: string }) =>
                        typeof name === "object" ? name?.ar : name,
                    },
                    {
                      title: t("price"),
                      dataIndex: "price",
                      key: "price",
                      render: (price: number) => price?.toLocaleString() ?? "-",
                    },
                    {
                      title: t("price_after_discount"),
                      dataIndex: "priceAfterDiscount",
                      key: "priceAfterDiscount",
                      render: (price: number) => price?.toLocaleString() ?? "-",
                    },
                    {
                      title: t("price_in_dollar"),
                      dataIndex: "price_in_dollar",
                      key: "price_in_dollar",
                      render: (price: number) => price != null ? `$${price}` : "-",
                    },
                    {
                      title: t("general_discount"),
                      dataIndex: "general_discount",
                      key: "general_discount",
                      render: (val: boolean, record: IQuantity) =>
                        val ? `${record.value_general_discount}%` : "-",
                    },
                    {
                      title: t("special_discount"),
                      dataIndex: "special_discount",
                      key: "special_discount",
                      render: (val: boolean, record: IQuantity) =>
                        val ? `${record.value_special_discount}%` : "-",
                    },
                    {
                      title: t("quantity"),
                      dataIndex: "quantity",
                      key: "quantity",
                    },
                    {
                      title: t("available"),
                      dataIndex: "available",
                      key: "available",
                      render: (val: boolean) =>
                        val ? <ImCheckmark color="green" /> : <ImCross color="red" />,
                    },
                    {
                      title: t("store_status"),
                      dataIndex: "status",
                      key: "status",
                      render: (val: number) => (
                        <Tag
                          color={getProductStatusColor(val)}
                          title={t(getProductStatus(val))}
                        />
                      ),
                    },
                  ]}
                />
              </Collapse.Panel>
            </Collapse>

            <Divider />
            <Descriptions size="small" column={1} bordered>

              <AntdDescriptions.Item label={t("image")}>
                <OldImage src={details?.image?.url} preview />
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("file")}>
                <a target="_blank" href={details?.pdf?.url}>
                  download pdf
                </a>
              </AntdDescriptions.Item>
            </Descriptions>

            <Divider />
            <Descriptions column={1} size="small" layout="vertical">
              <AntdDescriptions.Item label={t("attributes")} span={24}>
                <List bordered size="small" style={{ width: "100%" }}>
                  <ProductAttributes attributes={details?.attributes} />
                </List>
              </AntdDescriptions.Item>
            </Descriptions>
          </>
        )}
      </PageDetails>
    </>
  );
};

const ProductDetailesPage = () => {
  return (
    <ProductContextProvider>
      <Product />
    </ProductContextProvider>
  );
};
export default ProductDetailesPage;
